/* MethodologyStepperV2 — Esteira metodológica visual do Cadastro.
 *
 * Mostra 6 etapas com cores automáticas, barra de progresso geral e
 * painel inline de pendências. Clicar numa etapa rola até o bloco
 * correspondente do formulário.
 */
import { useMemo } from "react";
import { CheckCircle2, Circle, AlertTriangle, AlertOctagon, Loader2, ListChecks } from "lucide-react";
import { computeCadastroState, STATE_META, progressBucket } from "@/lib/cadastroValidation";

const ICONS = {
  empty: Circle,
  progress: Loader2,
  done: CheckCircle2,
  warning: AlertTriangle,
  blocked: AlertOctagon,
};

export default function MethodologyStepperV2({ form, onStepClick, activeStepId }) {
  const state = useMemo(() => computeCadastroState(form), [form]);
  const bucket = progressBucket(state.progress);

  const handleClick = (step, index) => {
    if (onStepClick) {
      onStepClick(step, index);
      return;
    }
    const el = document.getElementById(step.anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="card-mil p-4 sm:p-5 gold-accent-top"
      data-testid="methodology-stepper-v2"
    >
      {/* Header com progresso */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="min-w-0">
          <div className="label-mil text-[var(--gold)]">Esteira Metodológica</div>
          <div className="font-display font-bold text-base sm:text-lg mt-0.5">
            Progresso: <span className="text-[var(--gold)] tabular-nums">{state.progress}%</span>
            <span className={`text-sm font-normal ml-2 ${bucket.color}`}>· {bucket.label}</span>
          </div>
        </div>
        {state.canSubmit && (
          <span className="text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
            Pronto para envio à Revisão Humana
          </span>
        )}
      </div>

      {/* Barra de progresso */}
      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mb-5">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-500"
          style={{ width: `${state.progress}%` }}
          aria-label={`Progresso ${state.progress}%`}
          data-testid="methodology-progress-bar"
        />
      </div>

      {/* 6 etapas */}
      <ol className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="methodology-steps">
        {state.steps.map((step, idx) => {
          const meta = STATE_META[step.state];
          const Icon = ICONS[step.state];
          const isActive = activeStepId === step.id;
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => handleClick(step, idx)}
                aria-label={`Etapa ${idx + 1}: ${step.label} — ${meta.label}`}
                aria-current={isActive ? "step" : undefined}
                data-testid={`step-${step.id}`}
                data-state={step.state}
                data-active={isActive ? "true" : "false"}
                className={`w-full text-left rounded-lg border ${meta.border} ${meta.bg} p-2.5 transition hover:shadow-sm hover:-translate-y-px focus:outline-none focus:ring-2 focus:ring-amber-400/40 ${isActive ? "ring-2 ring-amber-400/70 shadow-md" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} className={`${meta.text} shrink-0 ${step.state === "progress" ? "animate-spin" : ""}`} />
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">
                    {idx + 1}
                  </span>
                </div>
                <div className="font-display font-semibold text-[13px] text-[var(--text)] leading-tight">
                  {step.label}
                </div>
                <div className={`text-[11px] mt-1 font-semibold ${meta.text}`}>
                  {meta.label}
                </div>
              </button>
            </li>
          );
        })}
      </ol>

      {/* Pendências inline */}
      <div className="mt-5 pt-4 border-t border-[var(--border)]">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks size={14} className="text-[var(--gold)]" />
          <span className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">
            Pendências do Cadastro
          </span>
        </div>
        {state.pendencies.length === 0 && state.warnings.length === 0 ? (
          <p className="text-sm text-emerald-700 dark:text-emerald-300" data-testid="pendencias-empty">
            Nenhuma pendência crítica. Registro pronto para revisão.
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="pendencias-list">
            {state.pendencies.map((p) => (
              <li key={`p-${p}`} className="flex items-start gap-2 text-[13px] text-rose-700 dark:text-rose-300">
                <AlertOctagon size={12} className="mt-0.5 shrink-0" /> {p}
              </li>
            ))}
            {state.warnings.map((w) => (
              <li key={`w-${w}`} className="flex items-start gap-2 text-[13px] text-amber-700 dark:text-amber-300">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
